import type { BirthChartForm } from "../components/BirthFields";

// R46 (ITEM 6's currency, R37's pattern) — **THE FORM EVERY ASTROLOGY READING IS ORDERED ON, AND THE
// ONE PLACE HER SUBJECT STAMP CAN FALL OFF IT.**
//
// `AstrologyReadingForm` is a hand-copied SUBSET of `BirthChartForm`, and being hand-copied is the
// whole reason this needed an instrument: it had already DRIFTED once. I6/R2's two stamp fields
// (`subjectKind`, `subjectId`) were missing from the type, so `readingFormFromChart` never copied
// them, and **every reading posted from this form arrived UNSTAMPED** — which drops the server back
// onto matching a saved person BY NAME, the exact fallback I6 exists to kill. It was found by
// `npx tsc --noEmit` (TS2339), not by eye, and not by any gate: a subset type that quietly loses a
// field does not throw, does not log, and renders a perfectly good reading about the wrong person.
//
// Out here the copy is arithmetic a gate RUNS: hand it a chart with every field set and assert the
// form comes back carrying each one, rather than reading the object literal and agreeing with it.
export type AstrologyReadingForm = {
  // The SAME subject stamp `BirthChartForm` carries. If you add a field to the stamp, it belongs
  // here and in the copier below, and the fixture will say so.
  subjectKind?: "profile" | "person";
  subjectId?: string;
  name: string;
  firstName: string;
  middleName: string;
  lastName: string;
  birthDate: string;
  birthTime: string;
  birthPeriod: "AM" | "PM";
  birthTimeUnknown: boolean;
  birthplace: string;
  birthRegion: string;
  birthCountry: string;
  latitude: string;
  longitude: string;
  timezone: string;
  question: string;
};

// Every field is defaulted, because a reading form is rendered into controlled inputs and an
// `undefined` there is React's uncontrolled-input warning followed by a field that will not type.
// The two stamp fields are the deliberate exception: absent means "no saved subject", which is a
// real answer and not an empty string.
export function readingFormFromChart(chart: BirthChartForm | null): AstrologyReadingForm {
  return {
    subjectKind: chart?.subjectKind,
    subjectId: chart?.subjectId,
    name: chart?.name || "",
    firstName: chart?.firstName || "",
    middleName: chart?.middleName || "",
    lastName: chart?.lastName || "",
    birthDate: chart?.birthDate || "",
    birthTime: chart?.birthTime || "",
    birthPeriod: chart?.birthPeriod || "AM",
    birthTimeUnknown: Boolean(chart?.birthTimeUnknown),
    birthplace: chart?.birthplace || "",
    birthRegion: chart?.birthRegion || "",
    birthCountry: chart?.birthCountry || "",
    latitude: chart?.latitude || "",
    longitude: chart?.longitude || "",
    timezone: chart?.timezone || "",
    // Her question rides under a different name on each side: `intention` on the chart, `question`
    // on the order. The one rename in this whole copy, and it is why the copy exists at all.
    question: chart?.intention || "",
  };
}

// R71's LINE PAYMENT (R5's own currency, and the reason that currency exists) — **THE ARITHMETIC
// THAT TURNS WHAT SHE TYPED INTO THE NUMBER EVERY CHART IS CAST ON.**
//
// It sat in App.tsx with ONE call site and no instrument at all: a pure law inside a TSX file cannot
// be imported by a `.mjs` gate, so the only thing that had ever run it was a member ordering a
// reading. It is the last step between her birth form and `/api/room/generate`, and its two hinges
// are the two that a reader's eye slides over — **midnight and noon**. `12:30 AM` is hour 0, not 12;
// `12:30 PM` is hour 12, not 24. Get either backwards and the cast is twelve hours out, which moves
// every house cusp and the Rising sign with them, and nothing on the screen looks wrong.
//
// **A REFUSAL IS `null`, NEVER A GUESSED HOUR** (R44's ruling one room over: an unknown value is
// refused, not replaced). The caller has its own answer for a birth time she does not know — noon,
// declared at the call site — and a silent fallback here would make "she told us midday" and "she
// typed something we could not read" the same chart.
// **AND RUNNING IT FOUND ONE, WHICH IS THE ARGUMENT FOR THE MOVE RATHER THAN A FOOTNOTE TO IT.**
// The old guard was `[rawHour, rawMinute] = time.split(":").map(Number)` with `Number.isNaN(rawMinute)`
// as the minute's only test — and a time carrying NO COLON (`"10"`) leaves `rawMinute` **undefined**,
// which is not NaN, is not `< 0` and is not `> 59`. It sailed through all three and returned
// `{ hour: 10, min: undefined }`, straight into the cast. The shape is the one this line keeps
// paying for (R55's `Array.isArray` over a fabricated array): **the guard that looks like it is
// checking the value is checking a different value's failure mode.** The clock is matched WHOLE now,
// so there is no half-read time left to guard.
export function convertToTwentyFourHour(time: string, period: "AM" | "PM") {
  const clock = /^(\d{1,2}):(\d{1,2})$/.exec(String(time || "").trim());
  if (!clock) return null;
  const rawHour = Number(clock[1]);
  const rawMinute = Number(clock[2]);
  // A 12-hour clock has no hour 0 and no hour 13.
  if (rawHour < 1 || rawHour > 12 || rawMinute < 0 || rawMinute > 59) {
    return null;
  }
  const hour = period === "PM" && rawHour !== 12 ? rawHour + 12 : period === "AM" && rawHour === 12 ? 0 : rawHour;
  return { hour, min: rawMinute };
}
